/* KPIs da tela de notas emitidas. Seguem os mesmos filtros de empresa e busca da lista
   (não o chip de status: as taxas comparam um status com os outros). */
import React from 'react';
import { Box, Flex, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { ArrowDownRight, ArrowUpRight } from 'phosphor-react';
import { INfseTotalizadores } from '../../../../../types/nfseEmissao';
import { brl, C, Card, CountUp, Eyebrow, Meter, MONO, Ring } from '../shared/ui';

export const pct = (v?: number | null, casas = 1) =>
  v == null
    ? '—'
    : `${(v * 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: casas,
      })}%`;

function Kpi({
  rotulo,
  carregando,
  valor,
  lado,
  children,
}: {
  rotulo: string;
  carregando: boolean;
  valor: React.ReactNode;
  lado?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <Card p={{ base: '14px', md: '18px 20px' }} minW={0}>
      <Flex justify="space-between" align="flex-start" gap={2}>
        <Box minW={0}>
          <Eyebrow fontSize="9.5px" letterSpacing="1px" noOfLines={1}>
            {rotulo}
          </Eyebrow>
          <Skeleton isLoaded={!carregando} mt={1} borderRadius="6px">
            <Text
              fontFamily={MONO}
              fontSize={{ base: '16px', md: '20px' }}
              lineHeight={{ base: '22px', md: '26px' }}
              fontWeight={600}
              color={C.ink}
              noOfLines={1}
            >
              {valor}
            </Text>
          </Skeleton>
        </Box>
        {!carregando && lado}
      </Flex>
      {!carregando && children && (
        <Box mt={2} fontSize="12px" lineHeight="16px" color={C.subtle}>
          {children}
        </Box>
      )}
    </Card>
  );
}

// Variação do mês contra os mesmos dias do mês anterior.
function Variacao({ valor }: { valor: number | null }) {
  if (valor == null) return <Text as="span">sem base no mês anterior</Text>;
  const subiu = valor >= 0;
  const Seta = subiu ? ArrowUpRight : ArrowDownRight;
  return (
    <Flex
      as="span"
      display="inline-flex"
      align="center"
      gap="2px"
      px="6px"
      borderRadius="999px"
      fontWeight={600}
      color={subiu ? C.successText : C.dangerText}
      bg={subiu ? C.successBg : C.dangerBg}
    >
      <Seta size={12} weight="bold" />
      {pct(Math.abs(valor))}
    </Flex>
  );
}

export function Totalizadores({ dados }: { dados?: INfseTotalizadores }) {
  const carregando = !dados;
  const d = dados;
  const aprovacao = (d?.taxa_aprovacao ?? 0) * 100;
  return (
    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={{ base: 2, md: 3 }} mb={{ base: 3, md: 4 }}>
      <Kpi
        rotulo="Valor autorizado"
        carregando={carregando}
        valor={<CountUp value={d?.valor_autorizado ?? 0} format={brl} />}
      >
        <Flex wrap="wrap" align="center" gap="4px">
          <Text as="span">Mês {brl(d?.valor_mes)}</Text>
          <Variacao valor={d?.variacao_mes ?? null} />
        </Flex>
      </Kpi>

      <Kpi
        rotulo="Ticket médio"
        carregando={carregando}
        valor={<CountUp value={d?.ticket_medio ?? 0} format={brl} />}
      >
        {d?.autorizadas ?? 0} nota(s) · {d?.tomadores ?? 0} tomador(es)
        <br />
        ISS {brl(d?.iss_autorizado)} · alíquota média {pct(d?.aliquota_media_iss, 2)}
      </Kpi>

      <Kpi
        rotulo="Aprovação na Sefin"
        carregando={carregando}
        valor={pct(d?.taxa_aprovacao)}
        lado={d?.taxa_aprovacao != null && <Ring value={aprovacao} size={40} stroke={4} />}
      >
        {d?.por_status?.rejeitada ?? 0} rejeitada(s) · {pct(d?.taxa_rejeicao)} das enviadas
      </Kpi>

      <Kpi rotulo="Cancelamentos" carregando={carregando} valor={pct(d?.taxa_cancelamento)}>
        <Meter value={(d?.taxa_cancelamento ?? 0) * 100} color={C.danger} />
        <Text mt="6px">
          {d?.por_status?.cancelada ?? 0} nota(s) · {brl(d?.valor_cancelado)}
        </Text>
      </Kpi>
    </SimpleGrid>
  );
}
